import { useCallback } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import type { RootState } from "@/store/store";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleSentryBeforeCapture } from "@/utils/error";
import useLayout from "@/viewModels/useLayout";
import MetaTags from "@/components/ui/meta/MetaTags";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Alerts from "@/components/ui/Alerts";
import Backdrop from "@/components/ui/backdrop/Backdrop";
import NewsTicker from "@/components/ui/NewsTicker";
import { SentryErrorFallback } from "@/components/ui/errors/SentryErrorFallback";

const Layout = () => {
  const { isNavigationHidden } = useLayout();
  const appDispatch = useAppDispatch();

  const pageLoading = useSelector((store: RootState) => store.pageLoading);
  const alertMessages = useSelector((store: RootState) => store.alertMessages);

  const handleRemoveAlert = useCallback((id: number) => appDispatch.removeAlert(id), [appDispatch]);

  return (
    <>
      <MetaTags />
      <Alerts messages={alertMessages} onRemove={handleRemoveAlert} />
      <Backdrop show={pageLoading.show} isBlocking={pageLoading.mode !== "non-blocking"} />
      <div className="flex min-h-screen flex-col bg-brand-cream">
        <Header isNavigationHidden={isNavigationHidden} />
        <div className="flex-grow">
          <Sentry.ErrorBoundary
            fallback={SentryErrorFallback}
            beforeCapture={handleSentryBeforeCapture}
          >
            <Outlet />
          </Sentry.ErrorBoundary>
        </div>
      </div>
      {!isNavigationHidden && (
        <>
          <NewsTicker />
          <Footer />
        </>
      )}
    </>
  );
};

export default Layout;
